import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getAppointments } from "@/api/services";
import Header from "@/components/Header";
import { 
  Users, Search, Phone, Video, MessageSquare, Clock, Calendar,
  ChevronRight, Loader2, StickyNote, X, Save, User
} from "lucide-react";
import { cn } from "@/utils/cn";
import { format } from "date-fns";

// Generate masked client ID from MongoDB ObjectId
const getMaskedId = (id) => {
  if (!id) return "Client";
  const shortId = id.toString().slice(-6).toUpperCase();
  return `#${shortId}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
};

// Client Card Component
const ClientCard = ({ client, onClick, isSelected }) => {
  const lastConsultation = client.consultations[0];
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left",
        isSelected ? "bg-orange-100 border border-orange-300" : "bg-gray-50 hover:bg-gray-100"
      )}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
        {client.picture ? (
          <img src={client.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <span className="text-sm font-medium">{getInitials(client.name)}</span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
          <span className="text-xs text-gray-400">{getMaskedId(client._id)}</span>
        </div>
        <p className="text-xs text-gray-500">
          {client.consultations.length} consultation{client.consultations.length !== 1 ? "s" : ""}
          {lastConsultation && (
            <span> · Last: {format(new Date(lastConsultation.date), "MMM d")}</span>
          )}
        </p>
      </div>

      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
    </button>
  );
};

// Client Detail Panel
const ClientDetail = ({ client, onClose, notes, onSaveNote }) => {
  const [noteText, setNoteText] = useState(notes[client._id] || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSaveNote(client._id, noteText);
    setSaving(false);
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case "video": return Video;
      case "chat": return MessageSquare;
      default: return Phone;
    }
  };

  return (
    <div className="bg-white rounded-lg h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
            {client.picture ? (
              <img src={client.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <span className="text-lg font-medium">{getInitials(client.name)}</span>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{client.name}</h3>
            <p className="text-sm text-gray-500">{getMaskedId(client._id)}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 p-4 border-b">
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-lg font-semibold text-gray-900">{client.consultations.length}</p>
          <p className="text-xs text-gray-500">Sessions</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-lg font-semibold text-gray-900">
            {client.consultations.reduce((sum, c) => sum + (c.duration || 0), 0)}m
          </p>
          <p className="text-xs text-gray-500">Total Time</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-lg font-semibold text-gray-900">
            {client.consultations[0] ? format(new Date(client.consultations[0].date), "MMM d") : "-"}
          </p>
          <p className="text-xs text-gray-500">Last Visit</p>
        </div>
      </div>

      {/* Notes Section */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-gray-500" />
            <h4 className="text-sm font-medium text-gray-900">Notes</h4>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600"
          >
            <Save className="h-3 w-3" />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add notes about this client (birth details, preferences, follow-ups...)"
          className="w-full p-2 text-sm border rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <p className="text-xs text-gray-400 mt-1">Notes are private and only visible to you</p>
      </div>

      {/* Consultation History */}
      <div className="flex-1 overflow-y-auto p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Consultation History</h4>
        <div className="space-y-2">
          {client.consultations.map((consultation) => {
            const ModeIcon = getModeIcon(consultation.mode);
            return (
              <div
                key={consultation._id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                  <ModeIcon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">
                      {consultation.typeofmeeting || "Consultation"}
                    </span>
                    <span className={cn(
                      "text-xs px-1.5 py-0.5 rounded",
                      consultation.status === "completed" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    )}>
                      {consultation.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar className="h-3 w-3" />
                    <span>{format(new Date(consultation.date), "MMM d, yyyy")}</span>
                    <span>·</span>
                    <span>{formatTime(consultation.time)}</span>
                    {consultation.duration > 0 && (
                      <>
                        <span>·</span>
                        <Clock className="h-3 w-3" />
                        <span>{consultation.duration}m</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function ClientsPage() {
  const { astrologer } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientNotes, setClientNotes] = useState({});

  useEffect(() => {
    if (!astrologer?.astrologerId || !astrologer?.accessToken) return;

    setLoading(true);
    getAppointments(astrologer.astrologerId, astrologer.accessToken)
      .then(({ data }) => {
        if (data?.appointments) {
          // Group appointments by client
          const clientMap = new Map();
          
          data.appointments.forEach(apt => {
            if (!apt.user?._id) return;
            
            const clientId = apt.user._id;
            if (!clientMap.has(clientId)) {
              clientMap.set(clientId, {
                _id: clientId,
                name: apt.user.name || "Unknown Client",
                picture: apt.user.picture,
                consultations: []
              });
            }
            
            clientMap.get(clientId).consultations.push({
              _id: apt._id,
              date: apt.date,
              time: apt.time,
              mode: apt.mode,
              status: apt.status,
              duration: apt.duration,
              typeofmeeting: apt.typeofmeeting
            });
          });

          // Sort consultations by date (newest first) and convert to array
          const clientList = Array.from(clientMap.values()).map(client => ({
            ...client,
            consultations: client.consultations.sort((a, b) => 
              new Date(b.date) - new Date(a.date)
            )
          }));

          // Sort clients by most recent consultation
          clientList.sort((a, b) => {
            const aDate = a.consultations[0]?.date || 0;
            const bDate = b.consultations[0]?.date || 0;
            return new Date(bDate) - new Date(aDate);
          });

          setClients(clientList);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    // Load saved notes from localStorage
    const savedNotes = localStorage.getItem(`clientNotes_${astrologer.astrologerId}`);
    if (savedNotes) {
      setClientNotes(JSON.parse(savedNotes));
    }
  }, [astrologer]);

  const handleSaveNote = async (clientId, note) => {
    const newNotes = { ...clientNotes, [clientId]: note };
    setClientNotes(newNotes);
    // Save to localStorage (in production, this would be an API call)
    localStorage.setItem(`clientNotes_${astrologer.astrologerId}`, JSON.stringify(newNotes));
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getMaskedId(client._id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Header name="Clients" />
      <div className="w-full p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-140px)]">
          {/* Client List */}
          <div className="lg:col-span-1 bg-white rounded-lg flex flex-col">
            {/* Search */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* Client List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No clients found</p>
                </div>
              ) : (
                filteredClients.map(client => (
                  <ClientCard
                    key={client._id}
                    client={client}
                    onClick={() => setSelectedClient(client)}
                    isSelected={selectedClient?._id === client._id}
                  />
                ))
              )}
            </div>

            {/* Summary */}
            <div className="p-3 border-t bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                {clients.length} client{clients.length !== 1 ? "s" : ""} · {" "}
                {clients.reduce((sum, c) => sum + c.consultations.length, 0)} total sessions
              </p>
            </div>
          </div>

          {/* Client Detail */}
          <div className={cn(
            "lg:col-span-2",
            !selectedClient && "hidden lg:flex lg:items-center lg:justify-center lg:bg-white lg:rounded-lg"
          )}>
            {selectedClient ? (
              <ClientDetail
                client={selectedClient}
                onClose={() => setSelectedClient(null)}
                notes={clientNotes}
                onSaveNote={handleSaveNote}
              />
            ) : (
              <div className="text-center text-gray-500">
                <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Select a client to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
